import React, { useRef, useState } from "react";
import Flicking from "@egjs/react-flicking";
import "@egjs/react-flicking/dist/flicking.css";
import qs from "qs";
import useSWR from "swr";
import apiClient from "@/utils/apiClient";
import AnchorChip from "@/components/lib/AnchorChip";
import Image from "next/image";
import Button from "@/components/actions/Button";
import Link from "next/link";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@heroicons/react/outline";
import * as Text from "@/components/text";
import PosterCard from "@/components/film/PosterCard";
import breakpoints from "@/constants/breakpoinst";
import useWindowSize from "@/hooks/useWindowSize";
import { parseISO, format } from "date-fns";
import { Navigation, Pagination, Thumbs, EffectFade } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import "swiper/css/effect-fade";
import { localeTexts } from "@/utils/locale";

export default function Generations({ locale }) {
  const windowSize = useWindowSize();
  const flickingRef = useRef(null);
  const beforeRef = useRef(null);
  const paginationRef = useRef(null);
  const afterRef = useRef(null);
  const [slideIndex, setIndex] = useState(0);
  const [indexFlicking, setIndexFlicking] = useState(0);
  const handleChangeIndex = (index) => {
    flickingRef.current.moveTo(index);
  };

  const q = qs.stringify({
    populate: {
      ama_title: "*",
      cover_image: "*",
      description: "*",
      ama_subtitle: "*",
      peliculas: {
        populate: {
          film_cover_image: "*",
          Directores: "*",
        },
      },
    },
    sort: ["id:desc"],
  });
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error, mutate } = useSWR(`/api/ama-generaciones?${q}`, fetcher);
  const generations = data?.data;

  return (
    <>
      {generations && (
        <>
          <div className="mt-8 amb-container lg:mt-20">
            <Text.Headline
              as="p"
              className="w-full text-2xl font-light md:pb-0 md:text-[32px] lg:mb-3"
            >
              {localeTexts[locale].common.generationsAndDocumentaries}
            </Text.Headline>
            <>
              <div className="w-full">
                <div className="flex justify-between w-full py-2 sm:pt-2 md:pt-4 lg:pt-8">
                  {windowSize.width > breakpoints.lg ? (
                    <button ref={beforeRef} className="z-50 left-8 ">
                      <ArrowLeftIcon className="w-4 h-4" />
                    </button>
                  ) : null}
                  <div
                    className="z-50 justify-start w-full cursor-pointer sm:grid sm:grid-cols-2 sm:gap-1 md:flex md:gap-2 md:px-2"
                    ref={paginationRef}
                  ></div>
                  {windowSize.width > breakpoints.lg ? (
                    <button ref={afterRef} className="right-auto">
                      <ArrowRightIcon className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
              </div>
            </>
          </div>
          <Swiper
            spaceBetween={30}
            effect={"fade"}
            loop={true}
            onActiveIndexChange={(swiper) => {
              setIndex(swiper.realIndex - 1);
            }}
            slidesPerView={1}
            modules={[EffectFade, Thumbs, Navigation, Pagination]}
            pagination={{
              clickable: true,
              type: "bullets",
              bulletClass: "my-bullet",
              el: paginationRef.current,
              renderBullet: function (index) {
                return (
                  '<div class="my-bullet px-2 py-1 rounded-full border border-neutral-400 active:text-white active:bg-neutral-500 ">' +
                  generations[index]?.attributes?.ama_title
                  +
                  "</div>"
                );
              },
              bulletActiveClass: "bg-black text-white",
            }}
            navigation={{
              enabled: false,
              nextEl: afterRef.current,
              prevEl: beforeRef.current,
              hiddenClass: "hidden",
              disabledClass: "text-neutral-50 border-neutral-50",
            }}
          >
            {generations.map((generation, indexGeneration) => {
              return (
                <SwiperSlide
                  key={generation.id}
                >
                  <div className={`bg-white flex-wrap  ${indexGeneration === slideIndex + 1 ? "flex" : 'hidden'}`} >

                    <div className="lg:amb-container mb-3 h-[193px] w-full md:h-[433px] md:px-8 lg:mb-5 lg:px-0">
                      <Image
                        width={1208}
                        height={633}
                        className="object-cover w-full h-full"
                        alt="Generation image"
                        src={
                          generation?.attributes?.cover_image?.data
                            ?.attributes?.url
                        }
                      />
                    </div>
                    <div className="mb-8 bg-white amb-container lg:mb-20 lg:flex">
                      <div className="lg:w-full">
                        <Text.Headline
                          as="span"
                          className="mb-1.5 text-center text-4xl font-bold md:text-left md:text-[40px]"
                        >
                          {generation?.attributes?.ama_title}
                        </Text.Headline>
                        <Text.Headline
                          as="p"
                          className="w-full mb-1 text-xl font-semibold text-center md:my-2 md:text-left"
                        >
                          {format(
                            new Date(
                              generation?.attributes?.date_start
                            ),
                            "yyyy"
                          )}
                          &nbsp;-&nbsp;
                          {format(
                            new Date(generation?.attributes?.date_end),
                            "yyyy"
                          )}
                        </Text.Headline>
                        <Text.Label
                          as="p"
                          className="w-full mb-3 text-center text-neutral-600 md:text-left md:text-lg"
                        >
                          {generation?.attributes?.ama_subtitle}
                        </Text.Label>
                      </div>
                      <div>
                        <Text.Noun
                          as="p"
                          className="w-full text-center md:text-left lg:text-lg"
                        >
                          {generation?.attributes?.description}
                        </Text.Noun>
                      </div>
                    </div>
                    <div className="bg-white md:amb-container md:grid md:grid-cols-2 md:gap-6 lg:gap-20 lg:px-20">
                      {generation?.attributes?.peliculas?.data?.map(
                        (movie, i) => (
                          <div key={i} className="pt-6 mb-8 md:mb-10 lg:mb-0 lg:pt-0">
                            <PosterCard
                              size={
                                windowSize.width > breakpoints.xl
                                  ? "xl"
                                  : windowSize.width > breakpoints.lg
                                    ? "lg"
                                    : windowSize.width > breakpoints.md
                                      ? "md"
                                      : windowSize.width > breakpoints.sm
                                        ? "sm"
                                        : "sm"
                              }
                              theme="light"
                              slug={`/catalogo/${movie?.attributes?.filmSlug}`}
                              film_image={
                                movie?.attributes?.film_cover_image.data?.attributes
                                  .formats.small?.url ||
                                movie?.attributes?.film_cover_image.data?.attributes.url
                              }
                              film_streaming_link={movie?.attributes?.film_stream_link}
                              film_trailer_link={
                                movie?.attributes?.film_trailer_link || ""
                              }
                            />
                            <div className="px-4">
                              <Text.Headline
                                as="p"
                                className="mt-4 pb-2 text-[28px] font-bold"
                              >
                                {movie?.attributes?.film_name}&nbsp;
                              </Text.Headline>
                              <div className="flex gap-3">
                                <Text.Paragraph
                                  as="p"
                                  className="text-lg font-semibold text-right"
                                >
                                  {localeTexts[locale].common.directedBy}
                                </Text.Paragraph>
                                <div>
                                  {movie?.attributes?.Directores.map((director, i) => (
                                    <Text.Paragraph
                                      as="p"
                                      className="text-lg font-light "
                                      key={i}
                                    >
                                      {director?.Director}
                                    </Text.Paragraph>
                                  ))}
                                </div>
                              </div>
                              {movie?.attributes?.ama_creators ? (
                                <div className="flex gap-3">
                                  <Text.Paragraph
                                    as="p"
                                    className="text-lg font-semibold text-right"
                                  >
                                    {localeTexts[locale].common.createdBy}
                                  </Text.Paragraph>
                                  <div>
                                    <Text.Paragraph as="p" className="text-lg font-light">
                                      {movie?.attributes?.ama_creators}
                                    </Text.Paragraph>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                    <div className="flex items-center justify-center w-full mt-6 mb-8 bg-white md:mb-16 md:mt-10 lg:mb-28 lg:mt-20">
                      <Link
                        passHref
                        href={"/catalogo?iniciativa=ambulante-mas-alla"}
                        className="flex justify-center w-full px-4"
                      >
                        <Button
                          size="lg"
                          color="secondary-light"
                          label={localeTexts[locale].common.viewAmaCollection}
                          className="w-full font-bold md:w-fit md:px-[60px]"
                          trailingIcon={<ArrowRightIcon />}
                        ></Button>
                      </Link>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </>
      )}
    </>
  );
}